import React, { useEffect, useRef, useState } from 'react';
import { useViewport } from '../../hooks/useViewport';
import { motion, AnimatePresence } from 'framer-motion';
import ClosedCaptionsClose from './ClosedCaptionsClose/ClosedCaptionsClose';
import ClosedCaptionsReturn from './ClosedCaptionsReturn/ClosedCaptionsReturn';
import LoadingSpinner from '../LoadingSpinner';

export type ClosedCaption = {
  name: string;
  caption: string;
  showName?: boolean;
};

export type ClosedCaptionsProps = {
  captions: ClosedCaption[];
  bottomOffset: number;
  onClose: () => void;
  translatedText: {
    closeText: string;
    returnText: string;
    waitingText: string;
  };
};

export const CLOSED_CAPTIONS_PADDING_OFFSET = 12;

const ClosedCaptions = ({
  captions,
  bottomOffset,
  onClose,
  translatedText,
}: ClosedCaptionsProps) => {
  const { isSmallViewport } = useViewport();
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  const { closeText, returnText, waitingText } = translatedText;

  const ref = useRef<HTMLDivElement>(null);
  const isUserScrolling = useRef(false);

  const scrollToBottom = () => {
    if (ref.current) {
      isUserScrolling.current = true;
      ref.current.scrollTop = ref.current.scrollHeight;
      setAutoScrollEnabled(true);
      setTimeout(() => {
        isUserScrolling.current = false;
      }, 100);
    }
  };

  // Handle scroll event to detect if user scrolled up
  const handleScroll = () => {
    if (isUserScrolling.current) {
      return;
    }

    if (ref.current) {
      const { scrollTop, scrollHeight, clientHeight } = ref.current;
      /**
       * Determines if the captions container is scrolled to the bottom.
       * The calculation checks if the distance from the bottom
       * (scrollHeight - scrollTop - clientHeight) is less than 10px.
       */
      const isScrolledToBottom =
        Math.abs(scrollHeight - scrollTop - clientHeight) < 10;

      setAutoScrollEnabled(isScrolledToBottom);
    }
  };

  // Auto-scroll to the latest caption if enabled
  useEffect(() => {
    if (ref.current && autoScrollEnabled) {
      isUserScrolling.current = true;
      ref.current.scrollTop = ref.current.scrollHeight;
      setTimeout(() => {
        isUserScrolling.current = false;
      }, 100);
    }
  }, [captions, autoScrollEnabled]);

  const cssVariables = {
    '--bottom-offset': `${bottomOffset}px`,
    '--container-width': isSmallViewport ? '100%' : '420px',
    '--cc-padding': `${CLOSED_CAPTIONS_PADDING_OFFSET}px`,
  } as React.CSSProperties;

  return (
    <div
      data-testid="closed-captions-container"
      className="closed-captions closed-captions-container"
      style={cssVariables}
    >
      <div className="closed-captions-panel">
        <div
          ref={ref}
          onScroll={handleScroll}
          className="closed-captions-scroll-container"
        >
          <div className="closed-captions-content">
            {captions.length === 0 && (
              <div
                data-testid="closed-captions-waiting"
                className="closed-captions-waiting"
              >
                <LoadingSpinner size="medium" />
                <p className="closed-captions-text closed-captions-text-medium">
                  {waitingText}
                </p>
              </div>
            )}

            {captions.length > 0 && (
              <div className="closed-captions-captions-wrapper">
                <div className="closed-captions-captions-list">
                  <AnimatePresence>
                    {captions.map(
                      ({ name, caption, showName = true }, index) =>
                        caption.trim() !== '' && (
                          <motion.div
                            key={index}
                            data-testid={`closed-captions-caption-${index}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                              duration: 0.3, // 300ms in seconds
                              ease: 'easeInOut',
                            }}
                            className="closed-captions-caption-item"
                          >
                            {showName && (
                              <p className="closed-captions-text closed-captions-text-small">
                                {name}
                              </p>
                            )}
                            <p className="closed-captions-text closed-captions-text-medium closed-captions-text-medium--bold">
                              {caption}
                            </p>
                          </motion.div>
                        )
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        </div>

        {!autoScrollEnabled && (
          <div
            data-testid="closed-captions-return-to-latest"
            className="closed-captions-return-to-latest"
          >
            <ClosedCaptionsReturn label={returnText} onClick={scrollToBottom} />
          </div>
        )}

        <div
          data-testid="closed-captions-close-container"
          className="closed-captions-close-container"
        >
          <ClosedCaptionsClose label={closeText} onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ClosedCaptions;
